import { normalizeToStartOfDayUTC } from '@/utils/date-formatters/date-formatters';

/**
 * Calculates the optimal date to pre-set in a date picker based on available date range.
 *
 * Logic:
 * 1. If today is within the range [startDateUtc, endDateUtc], return today
 * 2. If today is before the range, return startDateUtc (earliest future date)
 * 3. If today is after the range (all dates in past), return startDateUtc (earliest available date)
 * 4. If no endDateUtc, return today if >= startDateUtc, otherwise startDateUtc
 *
 * @param startDateUtc - The minimum available date (required)
 * @param endDateUtc - The maximum available date (optional, null means no upper limit)
 * @returns The optimal date to pre-set
 *
 * @example
 * // Today is Dec 19, range is Dec 14 - Dec 31
 * getOptimalPresetDate(new Date('2026-12-14'), new Date('2026-12-31'))
 * // Returns: Dec 19 (today is within range)
 *
 * @example
 * // Today is Dec 19, range starts Dec 21
 * getOptimalPresetDate(new Date('2026-12-21'), new Date('2026-12-31'))
 * // Returns: Dec 21 (today is before range)
 *
 * @example
 * // Today is Dec 19, range was Dec 14 - Dec 17 (past)
 * getOptimalPresetDate(new Date('2026-12-14'), new Date('2026-12-17'))
 * // Returns: Dec 14 (all dates in past)
 *
 * @example
 * // Today is Dec 19, no end date limit
 * getOptimalPresetDate(new Date('2026-12-14'), null)
 * // Returns: Dec 19 (today >= startDateUtc)
 */
export const getOptimalPresetDate = (startDateUtc: Date, endDateUtc: Date | null = null): Date => {
  const todayUtc = normalizeToStartOfDayUTC(new Date());

  if (endDateUtc) {
    // If today is within the range, use today
    if (todayUtc >= startDateUtc && todayUtc <= endDateUtc) {
      return todayUtc;
    }
    // If today is before the range, use startDateUtc
    else if (todayUtc < startDateUtc) {
      return startDateUtc;
    }
    // If today is after the range (all dates are in the past), use startDateUtc
    else {
      return startDateUtc;
    }
  } else {
    // No endDateUtc limit - use today if it's >= startDateUtc, otherwise use startDateUtc
    return todayUtc >= startDateUtc ? todayUtc : startDateUtc;
  }
};
